import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { SectionReveal } from "../components/Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "The 30-Day Window",
    body: "Every piece can be returned within 30 days of delivery — no questions, no restocking fees. The window starts the day your order is marked delivered, not the day it ships. Archive and Runway Edition pieces carry the same terms; limited does not mean final sale.",
  },
  {
    n: "02",
    title: "Condition Criteria",
    body: "Pieces must be unworn, unwashed and unaltered, with all tags, numbering cards and original packaging intact. Leather goods must show no creasing or hardware marks. We inspect every return within 48 hours of arrival at our studio.",
  },
  {
    n: "03",
    title: "How to Start a Return",
    body: "Look up your order on the Order Lookup page using your checkout email, then reply to your order confirmation email with the pieces you're sending back. We issue a prepaid, carbon-neutral return label within one business day. Drop it at any carrier point — done.",
  },
  {
    n: "04",
    title: "Refund Timing",
    body: "Once your return passes inspection, refunds are issued to the original payment method within 3–5 business days. Your bank may take an additional 2–7 days to post it. Partial returns are refunded per piece at the price you paid.",
  },
  {
    n: "05",
    title: "Exchanges & Repairs",
    body: "Need a different size? Exchanges ship free and skip the refund cycle entirely — your replacement is dispatched the day your return is scanned. And remember: every garment carries lifetime mending. A torn seam is a repair, not a return.",
  },
];

const FAQS = [
  { q: "Can I return a piece I bought on sale?", a: "Yes. Seasonal pricing never changes your right to return within 30 days. The refund reflects exactly what you paid." },
  { q: "What if my piece arrives damaged?", a: "Photograph it within 7 days of delivery and reply to your confirmation email. We ship a replacement immediately or refund in full — your choice, no return required for damaged goods." },
  { q: "Do you refund duties or taxes?", a: "Taxes collected at checkout are refunded in full. Import duties paid to your local customs authority are outside our control, but we'll provide any documentation your customs office needs to reclaim them." },
  { q: "How do exchanges work for limited editions?", a: "If your size is still in the archive, we reserve it the moment your return label is scanned. If it's gone, you choose: full refund or archive credit with 10% on top." },
];

const FaqItem = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-zinc-800">
      <button
        data-testid={testid}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left text-sm text-zinc-200 hover:text-[#FF334B] transition-colors"
      >
        {q}
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="pb-5 text-sm text-zinc-400 font-light leading-relaxed max-w-2xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Refunds() {
  return (
    <main data-testid="refunds-page" className="pt-16 min-h-screen">
      <div className="px-4 sm:px-8 lg:px-16 py-14 border-b border-zinc-900">
        <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase">Policy</p>
        <h1 className="font-display font-extrabold tracking-tighter uppercase text-4xl sm:text-5xl lg:text-6xl mt-3">
          Refunds <span className="font-editorial italic font-light normal-case text-[#FF334B]">&</span> Returns
        </h1>
        <p className="text-zinc-400 text-sm mt-5 font-light max-w-lg leading-relaxed">
          Thirty days. Prepaid label. Full refund. We make pieces built to keep — but if one isn't right, sending it back should be effortless.
        </p>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 py-16 max-w-5xl">
        {CHAPTERS.map((c, i) => (
          <SectionReveal key={c.n} delay={i * 0.05}>
            <div data-testid={`policy-chapter-${c.n}`} className="grid grid-cols-12 gap-6 py-8 border-t border-zinc-800/70">
              <p className="col-span-12 md:col-span-2 mono text-sm text-[#FF334B] tracking-[0.25em]">{c.n} /</p>
              <h2 className="col-span-12 md:col-span-4 font-display text-lg sm:text-xl font-semibold uppercase tracking-tight">{c.title}</h2>
              <p className="col-span-12 md:col-span-6 text-zinc-400 text-sm sm:text-base leading-relaxed font-light">{c.body}</p>
            </div>
          </SectionReveal>
        ))}
      </div>

      <div className="px-4 sm:px-8 lg:px-16 py-16 border-t border-zinc-900 max-w-5xl">
        <SectionReveal>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-10">Questions, Answered</h2>
        </SectionReveal>
        {FAQS.map((f, i) => (
          <FaqItem key={i} q={f.q} a={f.a} testid={`faq-item-${i}`} />
        ))}
      </div>

      <div className="px-4 sm:px-8 lg:px-16 py-20 border-t border-zinc-900 text-center">
        <SectionReveal>
          <p className="font-editorial italic text-2xl sm:text-3xl text-zinc-200">Ready to send something back?</p>
          <Link
            to="/orders"
            data-testid="start-return-button"
            className="inline-block mt-8 bg-[#FF334B] text-white px-10 py-4 mono text-xs tracking-[0.25em] uppercase hover:bg-[#d92339] transition-colors"
          >
            Find Your Order
          </Link>
        </SectionReveal>
      </div>
    </main>
  );
}
